import { useEffect, useState, useMemo } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { perplexityApi } from '../services/api';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Sparkles, ArrowLeft, Check, AlertTriangle, Loader2, RefreshCw } from 'lucide-react';
import { toast } from 'sonner';

const PerplexityJob = () => {
  const { jobId } = useParams();
  const navigate = useNavigate();
  const [job, setJob] = useState(null);
  const [resoluciones, setResoluciones] = useState({}); // {entidad_id||campo: "mantener"|"sobreescribir"}
  const [aplicando, setAplicando] = useState(false);

  const cargar = async () => {
    try { const j = await perplexityApi.getJob(jobId); setJob(j); }
    catch (e) { toast.error(e.message); }
  };
  useEffect(() => { cargar(); }, [jobId]);
  useEffect(() => {
    if (!job) return;
    if (['completado', 'completado_con_errores'].includes(job.estado) && job.conflictos?.length === 0) return;
    const t = setInterval(cargar, 2000);
    return () => clearInterval(t);
  }, [job?.estado, job?.conflictos?.length]);

  const porcentaje = useMemo(() => {
    if (!job?.total) return 0;
    return Math.round(((job.procesadas + job.fallidas) / job.total) * 100);
  }, [job]);

  const marcar = (entidad_id, campo, accion) => {
    setResoluciones({ ...resoluciones, [`${entidad_id}||${campo}`]: accion });
  };

  const aplicar = async () => {
    const res = Object.entries(resoluciones).map(([k, accion]) => {
      const [entidad_id, campo] = k.split('||');
      const conf = job.conflictos.find((c) => c.entidad_id === entidad_id && c.campo === campo);
      return { entidad_id, campo, accion, valor_nuevo: conf?.valor_nuevo };
    });
    if (res.length === 0) { toast.info('No hay resoluciones marcadas'); return; }
    setAplicando(true);
    try {
      const r = await perplexityApi.aplicarResoluciones(jobId, res);
      toast.success(`Aplicadas ${r.aplicadas} modificaciones · ${r.conflictos_restantes} conflictos pendientes`);
      setResoluciones({});
      cargar();
    } catch (e) { toast.error(e.message); }
    finally { setAplicando(false); }
  };

  const aplicarTodos = (accion) => {
    const next = { ...resoluciones };
    job.conflictos.forEach((c) => { next[`${c.entidad_id}||${c.campo}`] = accion; });
    setResoluciones(next);
  };

  if (!job) return <div className="p-6 text-ink-400">Cargando…</div>;

  const enCurso = ['pendiente', 'en_curso'].includes(job.estado);

  return (
    <div data-testid="perplexity-job-page">
      <button onClick={() => navigate('/clientes')} className="text-sm text-ink-500 hover:text-ink-700 inline-flex items-center gap-1 mb-4">
        <ArrowLeft className="w-4 h-4" /> Volver a clientes
      </button>
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-heading font-bold text-ink-900 flex items-center gap-2">
            <Sparkles className="w-6 h-6 text-violet-600" /> Enriquecimiento masivo
          </h1>
          <p className="text-ink-500 text-sm mt-1">Job {jobId.slice(0, 8)}… · Modo: <strong>{job.modo}</strong> · Campos: {job.campos.join(', ')}</p>
        </div>
        <Badge className={`${enCurso ? 'bg-sky-50 border-sky-200 text-sky-700' : 'bg-emerald-50 border-emerald-200 text-emerald-700'} border text-sm`}>
          {enCurso ? <><Loader2 className="w-3.5 h-3.5 inline mr-1 animate-spin" /> {job.estado}</> : <><Check className="w-3.5 h-3.5 inline mr-1" /> {job.estado}</>}
        </Badge>
      </div>

      <Card className="p-4 mb-4 border-ink-200 bg-white">
        <div className="flex items-center justify-between mb-2 text-sm">
          <span className="font-medium text-ink-700">Progreso</span>
          <span className="text-ink-500">{job.procesadas + job.fallidas} / {job.total}</span>
        </div>
        <div className="h-3 bg-ink-100 rounded-full overflow-hidden" data-testid="progress-bar">
          <div className="h-full bg-violet-600 transition-all" style={{ width: `${porcentaje}%` }} />
        </div>
        <div className="flex gap-4 mt-3 text-xs">
          <span className="text-emerald-700">✓ {job.aplicadas?.length || 0} actualizadas</span>
          <span className="text-amber-700">⚠ {job.conflictos?.length || 0} conflictos</span>
          <span className="text-red-700">✗ {job.fallidas || 0} errores</span>
          <button onClick={cargar} className="ml-auto text-ink-500 hover:text-ink-700 inline-flex items-center gap-1"><RefreshCw className="w-3 h-3" /> Refrescar</button>
        </div>
      </Card>

      {job.conflictos?.length > 0 && (
        <Card className="p-4 border-ink-200 bg-white" data-testid="tabla-conflictos">
          <div className="flex items-center justify-between mb-3">
            <h2 className="font-heading font-semibold text-ink-900 text-lg">
              <AlertTriangle className="w-5 h-5 text-amber-600 inline mr-1" />
              {job.conflictos.length} conflictos pendientes de resolución
            </h2>
            <div className="flex gap-2">
              <Button size="sm" variant="outline" onClick={() => aplicarTodos('mantener')} data-testid="btn-resolver-mantener-todos">Mantener todo</Button>
              <Button size="sm" variant="outline" onClick={() => aplicarTodos('sobreescribir')} className="border-violet-200 text-violet-700" data-testid="btn-resolver-sobreescribir-todos">Sobrescribir todo</Button>
              <Button size="sm" onClick={aplicar} disabled={aplicando || Object.keys(resoluciones).length === 0} className="bg-violet-600 hover:bg-violet-700 text-white" data-testid="btn-aplicar-resoluciones">
                {aplicando ? <Loader2 className="w-4 h-4 mr-1.5 animate-spin" /> : <Check className="w-4 h-4 mr-1.5" />}
                Aplicar {Object.keys(resoluciones).length || ''}
              </Button>
            </div>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-ink-50 text-ink-600 text-xs uppercase tracking-wider">
                <tr>
                  <th className="px-2 py-2 text-left">Empresa</th>
                  <th className="px-2 py-2 text-left">Campo</th>
                  <th className="px-2 py-2 text-left">Valor actual</th>
                  <th className="px-2 py-2 text-left">Perplexity ✦</th>
                  <th className="px-2 py-2 text-center">Decisión</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-ink-100">
                {job.conflictos.map((c, i) => {
                  const key = `${c.entidad_id}||${c.campo}`;
                  const decision = resoluciones[key];
                  return (
                    <tr key={i} className={decision === 'sobreescribir' ? 'bg-violet-50/40' : decision === 'mantener' ? 'bg-ink-50' : ''}>
                      <td className="px-2 py-2 font-medium text-ink-900 max-w-xs truncate">{c.razon_social}</td>
                      <td className="px-2 py-2 text-ink-700">{c.campo}</td>
                      <td className="px-2 py-2 text-ink-500 max-w-xs truncate" title={String(c.valor_actual)}>{String(c.valor_actual)}</td>
                      <td className="px-2 py-2 text-ink-900 max-w-xs truncate" title={String(c.valor_nuevo)}>{String(c.valor_nuevo)}</td>
                      <td className="px-2 py-2 text-center">
                        <div className="inline-flex border border-ink-200 rounded overflow-hidden text-xs">
                          <button
                            onClick={() => marcar(c.entidad_id, c.campo, 'mantener')}
                            className={`px-2 py-0.5 ${decision === 'mantener' ? 'bg-ink-700 text-white' : 'hover:bg-ink-100'}`}
                            data-testid={`res-mantener-${i}`}
                          >Mantener</button>
                          <button
                            onClick={() => marcar(c.entidad_id, c.campo, 'sobreescribir')}
                            className={`px-2 py-0.5 ${decision === 'sobreescribir' ? 'bg-violet-600 text-white' : 'hover:bg-violet-50 text-violet-700'}`}
                            data-testid={`res-sobreescribir-${i}`}
                          >Sobrescribir</button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </Card>
      )}

      {job.estado.startsWith('completado') && job.conflictos?.length === 0 && (
        <Card className="p-6 text-center border-emerald-200 bg-emerald-50">
          <Check className="w-10 h-10 text-emerald-600 mx-auto mb-2" />
          <p className="font-semibold text-emerald-900">Enriquecimiento completado</p>
          <p className="text-sm text-emerald-800 mt-1">
            {job.aplicadas?.length || 0} empresas actualizadas, {job.fallidas || 0} errores.
          </p>
        </Card>
      )}
    </div>
  );
};

export default PerplexityJob;
